import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { ScrollArea } from '../components/ui/scroll-area';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';
import {
  Crown, Plus, Sparkles, Clock, CheckCircle2, AlertCircle,
  FileText, Image, Video, Mail, Presentation, BarChart3,
  Building2, User, Package, Moon, X, Loader2, ExternalLink, Target, Link2, Bot
} from 'lucide-react';
import api from '../api';

const deliverableIcons = {
  'type': FileText,
  'image': Image,
  'video': Video,
  'mail': Mail,
  'presentation': Presentation,
  'file-text': BarChart3,
};

export const LeadershipAIWidget = () => {
  const navigate = useNavigate();
  const [agent, setAgent] = useState(null);
  const [stats, setStats] = useState(null);
  const [recentTasks, setRecentTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [campaigns, setCampaigns] = useState([]);
  const [deliverableTypes, setDeliverableTypes] = useState([]);
  const [creating, setCreating] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    context: '',
    deliverable_types: [],
    deliverable_context: '',
    add_to_overnight_queue: false,
    priority: 'medium',
    linked_campaign_id: '',
    assigned_agent_id: '',  // New: AI agent assignment
  });
  const [aiAgents, setAiAgents] = useState([]);  // New: AI agents list

  useEffect(() => {
    fetchAgentData();
    fetchDeliverableTypes();
    fetchCampaigns();
    fetchAIAgents();  // New: Fetch AI agents
  }, []);

  const fetchAgentData = async () => {
    try {
      const response = await api.get('/leadership-ai/agent');
      setAgent(response.data.agent);
      setStats(response.data.stats);
      setRecentTasks(response.data.recent_tasks);
    } catch (error) {
      console.error('Failed to fetch leadership AI data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDeliverableTypes = async () => {
    try {
      const response = await api.get('/deliverable-types');
      setDeliverableTypes(response.data);
    } catch (error) {
      console.error('Failed to fetch deliverable types:', error);
    }
  };

  const fetchCampaigns = async () => {
    try {
      const response = await api.get('/campaigns?status=active&limit=50');
      setCampaigns(response.data.campaigns || response.data || []);
    } catch (error) {
      console.error('Failed to fetch campaigns:', error);
    }
  };

  const fetchAIAgents = async () => {
    try {
      const response = await api.get('/leadership-ai/available-agents');
      setAiAgents(response.data || []);
    } catch (error) {
      console.error('Failed to fetch AI agents:', error);
    }
  };

  const handleCreateTask = async () => {
    if (!formData.title.trim()) {
      toast.error('Please enter a task title');
      return;
    }
    if (!formData.description.trim()) {
      toast.error('Please enter a task description');
      return;
    }

    setCreating(true);
    try {
      // Build the task data with campaign link if selected
      const taskData = { ...formData };
      if (taskData.linked_campaign_id && taskData.linked_campaign_id !== '__none__') {
        taskData.linked_campaign_ids = [taskData.linked_campaign_id];
      }
      delete taskData.linked_campaign_id;
      
      // Handle agent assignment
      if (taskData.assigned_agent_id === '__none__') {
        delete taskData.assigned_agent_id;
      }
      
      const response = await api.post('/leadership-tasks', taskData);
      
      if (response.data.blockers && response.data.blockers.length > 0) {
        toast.warning(
          `Task created with ${response.data.blockers.length} blocker(s) that need human action`,
          { duration: 5000 }
        );
      } else if (response.data.added_to_queue) {
        toast.success('Task created and added to overnight queue!');
      } else {
        toast.success('Task created successfully!');
      }
      
      // Dispatch event to notify other components
      window.dispatchEvent(new CustomEvent('leadership-task-created', { 
        detail: { taskId: response.data?.id, task: response.data } 
      }));
      
      setShowCreateDialog(false);
      setFormData({
        title: '',
        description: '',
        context: '',
        deliverable_types: [],
        deliverable_context: '',
        add_to_overnight_queue: false,
        priority: 'medium',
        linked_campaign_id: '',
        assigned_agent_id: '',
      });
      fetchAgentData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create task');
    } finally {
      setCreating(false);
    }
  };

  const toggleDeliverableType = (typeId) => {
    setFormData(prev => ({
      ...prev,
      deliverable_types: prev.deliverable_types.includes(typeId)
        ? prev.deliverable_types.filter(id => id !== typeId)
        : [...prev.deliverable_types, typeId]
    }));
  };

  const getStatusBadge = (status) => {
    const config = {
      pending: { variant: 'outline', className: 'bg-blue-50 text-blue-700 border-blue-200' },
      in_progress: { variant: 'outline', className: 'bg-amber-50 text-amber-700 border-amber-200' },
      completed: { variant: 'outline', className: 'bg-green-50 text-green-700 border-green-200' },
      blocked: { variant: 'outline', className: 'bg-red-50 text-red-700 border-red-200' },
      draft: { variant: 'outline', className: 'bg-slate-50 text-slate-700 border-slate-200' },
    };
    const c = config[status] || config.draft;
    return <Badge variant={c.variant} className={c.className}>{status}</Badge>;
  };

  if (loading) {
    return (
      <Card className="border-violet-200 bg-gradient-to-br from-violet-50 to-purple-50">
        <CardContent className="pt-6 flex items-center justify-center h-48">
          <Loader2 className="h-8 w-8 animate-spin text-violet-600" />
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card className="border-violet-200 bg-gradient-to-br from-violet-50 to-purple-50" data-testid="leadership-ai-widget">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div 
                className="h-12 w-12 rounded-full flex items-center justify-center"
                style={{ backgroundColor: agent?.color || '#8B5CF6' }}
              >
                <Crown className="h-6 w-6 text-white" />
              </div>
              <div>
                <CardTitle className="flex items-center gap-2">
                  {agent?.display_name || 'Leadership AI'}
                  <Sparkles className="h-4 w-4 text-amber-500" />
                </CardTitle>
                <CardDescription className="text-sm">
                  {agent?.description || 'Orchestrates tasks and coordinates AI agents'}
                </CardDescription>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button 
                onClick={() => navigate('/ai/leadership')}
                variant="outline"
                className="border-violet-200 text-violet-700 hover:bg-violet-50"
                data-testid="view-all-leadership-tasks-btn"
              >
                <ExternalLink className="h-4 w-4 mr-2" />
                View All
              </Button>
              <Button 
                onClick={() => setShowCreateDialog(true)}
                className="bg-violet-600 hover:bg-violet-700"
                data-testid="create-leadership-task-btn"
              >
                <Plus className="h-4 w-4 mr-2" />
                New Task
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Quick Stats */}
          <div className="grid grid-cols-4 gap-4 mb-4">
            <div className="text-center p-3 bg-white/60 rounded-lg">
              <p className="text-2xl font-bold text-violet-700">{stats?.total_tasks || 0}</p>
              <p className="text-xs text-muted-foreground">Total Tasks</p>
            </div>
            <div className="text-center p-3 bg-white/60 rounded-lg">
              <p className="text-2xl font-bold text-blue-600">{stats?.pending_tasks || 0}</p>
              <p className="text-xs text-muted-foreground">Pending</p>
            </div>
            <div className="text-center p-3 bg-white/60 rounded-lg">
              <p className="text-2xl font-bold text-green-600">{stats?.completed_tasks || 0}</p>
              <p className="text-xs text-muted-foreground">Completed</p>
            </div>
            <div className="text-center p-3 bg-white/60 rounded-lg">
              <p className="text-2xl font-bold text-red-600">{stats?.blocked_tasks || 0}</p>
              <p className="text-xs text-muted-foreground">Blocked</p>
            </div>
          </div>

          {/* Recent Tasks */}
          {recentTasks.length > 0 && (
            <div className="space-y-2">
              <h4 className="text-sm font-medium text-slate-700">Recent Tasks</h4>
              {recentTasks.slice(0, 3).map((task) => (
                <div 
                  key={task.id}
                  className="flex items-center justify-between p-2 bg-white/60 rounded-lg"
                >
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <span className="text-sm font-medium truncate">{task.title}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {getStatusBadge(task.status)}
                    <span className="text-xs text-muted-foreground">
                      {formatDistanceToNow(new Date(task.created_at), { addSuffix: true })}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}

          {recentTasks.length === 0 && (
            <div className="text-center py-6 text-muted-foreground">
              <Sparkles className="h-8 w-8 mx-auto mb-2 text-violet-300" />
              <p className="text-sm">No tasks yet. Create your first task to get started!</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Task Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Crown className="h-5 w-5 text-violet-600" />
              Create Leadership AI Task
            </DialogTitle>
            <DialogDescription>
              Create a task for the Leadership AI to orchestrate and complete. The AI will validate requirements and coordinate with other agents as needed.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Title */}
            <div className="space-y-2">
              <Label htmlFor="title">Task Title *</Label>
              <Input
                id="title"
                placeholder="e.g., Create Q1 Marketing Campaign"
                value={formData.title}
                onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                data-testid="task-title-input"
              />
            </div>

            {/* Description */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="description">Description *</Label>
                <span className={`text-xs ${formData.description.length > 1800 ? 'text-red-500' : 'text-muted-foreground'}`}>
                  {formData.description.length}/2000
                </span>
              </div>
              <Textarea
                id="description"
                placeholder="Describe what you need accomplished..."
                rows={3}
                value={formData.description}
                onChange={(e) => {
                  if (e.target.value.length <= 2000) {
                    setFormData(prev => ({ ...prev, description: e.target.value }));
                  }
                }}
                maxLength={2000}
                data-testid="task-description-input"
              />
              <p className="text-xs text-muted-foreground">
                💡 Tip: Include keywords like &quot;call&quot;, &quot;meeting&quot;, or &quot;approval&quot; if human action is required.
              </p>
            </div>

            {/* Additional Context */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="context">Additional Context</Label>
                <span className={`text-xs ${formData.context.length > 900 ? 'text-red-500' : 'text-muted-foreground'}`}>
                  {formData.context.length}/1000
                </span>
              </div>
              <Textarea
                id="context"
                placeholder="Any additional context, requirements, or constraints..."
                rows={2}
                value={formData.context}
                onChange={(e) => {
                  if (e.target.value.length <= 1000) {
                    setFormData(prev => ({ ...prev, context: e.target.value }));
                  }
                }}
                maxLength={1000}
              />
            </div>

            {/* Deliverable Types */}
            <div className="space-y-2">
              <Label>Required Deliverables</Label>
              <p className="text-xs text-muted-foreground mb-2">
                Select the types of deliverables needed for this task (click to add)
              </p>
              <div className="flex flex-wrap gap-2">
                {deliverableTypes.map((type) => {
                  const IconComponent = deliverableIcons[type.icon] || FileText;
                  const isSelected = formData.deliverable_types.includes(type.id);
                  return (
                    <button
                      key={type.id}
                      type="button"
                      onClick={() => toggleDeliverableType(type.id)}
                      className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition-all ${
                        isSelected 
                          ? 'border-violet-500 bg-violet-50 text-violet-700' 
                          : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                      }`}
                    >
                      <IconComponent className="h-4 w-4" style={{ color: type.color }} />
                      <span className="text-sm font-medium">{type.name}</span>
                      {isSelected && <CheckCircle2 className="h-4 w-4 text-violet-600" />}
                    </button>
                  );
                })}
                <button
                  type="button"
                  className="flex items-center gap-2 px-3 py-2 rounded-lg border border-dashed border-slate-300 hover:border-slate-400 text-slate-500 hover:text-slate-600"
                >
                  <Plus className="h-4 w-4" />
                  <span className="text-sm">Add Type</span>
                </button>
              </div>
            </div>

            {/* Deliverable Context */}
            {formData.deliverable_types.length > 0 && (
              <div className="space-y-2">
                <Label htmlFor="deliverable_context">Deliverable Requirements</Label>
                <Textarea
                  id="deliverable_context"
                  placeholder="Describe specific requirements for the deliverables..."
                  rows={2}
                  value={formData.deliverable_context}
                  onChange={(e) => setFormData(prev => ({ ...prev, deliverable_context: e.target.value }))}
                />
              </div>
            )}

            {/* Priority */}
            <div className="space-y-2">
              <Label>Priority</Label>
              <Select 
                value={formData.priority} 
                onValueChange={(value) => setFormData(prev => ({ ...prev, priority: value }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="critical">Critical</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Link to Campaign */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Link2 className="h-4 w-4" />
                Link to Campaign (Optional)
              </Label>
              <Select 
                value={formData.linked_campaign_id} 
                onValueChange={(value) => setFormData(prev => ({ ...prev, linked_campaign_id: value }))}
              >
                <SelectTrigger data-testid="campaign-link-select">
                  <SelectValue placeholder="Select a campaign to link..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">No campaign - standalone task</SelectItem>
                  {campaigns.map((campaign) => (
                    <SelectItem key={campaign.id} value={campaign.id}>
                      <div className="flex items-center gap-2">
                        <Target className="h-4 w-4" />
                        {campaign.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Link this task to a marketing campaign for cross-module visibility
              </p>
            </div>

            {/* Assign AI Agent */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Bot className="h-4 w-4" />
                Assign AI Agent (Optional)
              </Label>
              <Select 
                value={formData.assigned_agent_id} 
                onValueChange={(value) => setFormData(prev => ({ ...prev, assigned_agent_id: value }))}
              >
                <SelectTrigger data-testid="agent-assign-select">
                  <SelectValue placeholder="Select an AI agent to assign..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">No agent - unassigned</SelectItem>
                  {aiAgents.map((agent) => (
                    <SelectItem key={agent.id} value={agent.id}>
                      <div className="flex items-center gap-2">
                        <Bot className="h-4 w-4 text-violet-600" />
                        {agent.display_name || agent.name}
                        {(agent.specializations?.length > 0 || agent.specialty) && (
                          <span className="text-xs text-muted-foreground">- {agent.specializations?.[0]?.replace(/_/g, ' ') || agent.specialty}</span>
                        )}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Assign an AI agent to work on this task automatically
              </p>
            </div>

            {/* Overnight Queue */}
            <div className="flex items-center space-x-2 p-3 bg-slate-50 rounded-lg">
              <Checkbox
                id="overnight"
                checked={formData.add_to_overnight_queue}
                onCheckedChange={(checked) => setFormData(prev => ({ ...prev, add_to_overnight_queue: checked }))}
                data-testid="overnight-queue-checkbox"
              />
              <div className="flex-1">
                <Label htmlFor="overnight" className="flex items-center gap-2 cursor-pointer">
                  <Moon className="h-4 w-4 text-indigo-600" />
                  Add to Overnight Queue
                </Label>
                <p className="text-xs text-muted-foreground">
                  Task will be processed automatically after 4:30 PM
                </p>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleCreateTask} 
              disabled={creating}
              className="bg-violet-600 hover:bg-violet-700"
              data-testid="submit-leadership-task-btn"
            >
              {creating ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <Sparkles className="h-4 w-4 mr-2" />
                  Create Task
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default LeadershipAIWidget;
